import { useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useTranslation } from 'react-i18next'
import { Icon } from '@iconify/react'
import { Container, Lines, Reveal, PageHeader } from '@/components/ui'
import { SITE } from '@/content/site'
import { products } from '@/content/products'
import SEO from '@/utils/seo'
import { buildBreadcrumbList } from '@/utils/structuredData'
import { oneLine } from '@/utils/seoText'

type ContactFormData = {
  name: string
  organization: string
  email: string
  phone?: string
  message: string
  interests?: string[]
  /**
   * Honeypot. Hidden from people, filled in by bots. The server answers 200
   * and stores nothing when this is non-empty, so a bot learns nothing.
   */
  website?: string
}

export default function ContactPage() {
  const { t, i18n } = useTranslation()
  const [submitted, setSubmitted] = useState(false)
  /** Set when the enquiry could NOT be stored, so we can say so honestly. */
  const [submitError, setSubmitError] = useState<string | null>(null)

  const contactSchema = useMemo(
    () =>
      z.object({
        name: z.string().min(2, t('contact.form.errors.name')),
        organization: z.string().min(2, t('contact.form.errors.org')),
        email: z.string().email(t('contact.form.errors.email')),
        phone: z.string().optional(),
        message: z.string().min(10, t('contact.form.errors.message')),
        interests: z.array(z.string()).optional(),
      }),
    // i18n.language + t: re-validate when locale changes so error messages
    // re-render in the active language.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [i18n.language, t],
  )

  const {
    register,
    handleSubmit,
    setFocus,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<ContactFormData>({
    resolver: zodResolver(contactSchema),
    defaultValues: { interests: [] },
    // Per-field error messages re-render in the active language thanks to
    // the schema's dependency on `t`, but `mode` lets us flag them on the
    // first onSubmit attempt so the missing focus hop can fire.
    mode: 'onSubmit',
  })

  // `handleSubmit(onValid, onInvalid)` — onInvalid runs when zod rejects
  // the input. Without this, "submit" silently fails for a sighted user
  // (just shows red borders), and for a screen-reader user focus stayed
  // on the still-broken submit button.
  const onInvalid = () => {
    // Order matches the schema's declared fields so the user lands on
    // the first invalid one. `setFocus` is rhf-native and avoids fragile
    // DOM querying.
    const order: Array<keyof ContactFormData> = ['name', 'organization', 'email', 'message']
    for (const k of order) {
      if (errors[k]) {
        setFocus(k)
        return
      }
    }
  }

  const onSubmit = async (data: ContactFormData) => {
    setSubmitError(null)
    try {
      const res = await fetch('/api/cms/public/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...data, locale: i18n.language }),
      })
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as { error?: string } | null
        setSubmitError(
          body?.error === 'rate_limited'
            ? t('contact.form.errors.rateLimited')
            : t('contact.form.errors.submitFailed'),
        )
        return
      }
      // Only now is it true: the enquiry is stored and the team can read it.
      setSubmitted(true)
      reset()
      setTimeout(() => setSubmitted(false), 8000)
    } catch {
      // Network failure, CORS, the server restarting. The visitor has NOT been
      // recorded, so do not claim otherwise.
      setSubmitError(t('contact.form.errors.submitFailed'))
    }
  }

  return (
    <>
      <SEO
        title={t('seo.contactTitle')}
        description={t('seo.contactDescription')}
        keywords={t('seo.contactKeywords')}
        jsonLd={buildBreadcrumbList([
          { name: t('nav.home'), path: '/' },
          { name: oneLine(t('nav.contact')), path: '/contact' },
        ])}
      />
      <PageHeader
        title={<Lines text={t('contact.hero.title')} />}
        description={t('contact.hero.description')}
      />

      <section className="py-section-y px-section-x bg-surface">
        <Container>
          <div className="grid lg:grid-cols-5 gap-12">
            {/* Form */}
            <div className="lg:col-span-3">
              <Reveal>
                <div className="bg-surface-2 rounded-yada-2xl p-card md:p-8 border border-border shadow-yada">
                  <h2 className="text-2xl font-bold text-ink mb-6">{t('contact.form.title')}</h2>

                  <p className="text-ink-soft leading-relaxed mb-6">
                    <Icon icon="lucide:mails" className="inline text-primary mr-1" />
                    {t('contact.intro')}
                  </p>

                  {submitted && (
                    <div
                      role="status"
                      className="mb-6 p-4 bg-primary/10 border border-primary rounded-yada-lg flex items-center gap-3"
                    >
                      <Icon icon="lucide:check-circle-2" className="text-primary text-2xl" />
                      <div>
                        <p className="font-bold text-ink">{t('contact.form.successTitle')}</p>
                        <p className="text-sm text-ink-soft">{t('contact.form.successText')}</p>
                      </div>
                    </div>
                  )}

                  {submitError && (
                    <div
                      role="alert"
                      className="mb-6 p-4 bg-danger/10 border border-danger rounded-yada-lg flex items-start gap-3"
                    >
                      <Icon icon="lucide:circle-alert" className="text-danger text-2xl" />
                      <div>
                        <p className="font-bold text-ink">{t('contact.form.errors.submitTitle')}</p>
                        <p className="text-sm text-ink-soft">{submitError}</p>
                        <p className="text-sm text-ink-soft mt-1">
                          {t('contact.form.errors.fallback')}
                        </p>
                      </div>
                    </div>
                  )}

                  <form onSubmit={handleSubmit(onSubmit, onInvalid)} className="space-y-6" noValidate>
                    {/*
                      Honeypot. `sr-only` + `tabIndex={-1}` + `aria-hidden` keeps
                      it out of the tab order and the accessibility tree, so a
                      screen-reader user is not offered an inexplicable field.
                      It is still in the DOM and still submitted — which is the
                      point.
                    */}
                    <div className="sr-only" aria-hidden>
                      <label htmlFor="website">Website</label>
                      <input
                        id="website"
                        type="text"
                        tabIndex={-1}
                        autoComplete="off"
                        {...register('website')}
                      />
                    </div>
                    {/* Name + Organization */}
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="name" className="block text-sm font-semibold text-ink mb-2">
                          {t('contact.form.name')} <span className="text-error">*</span>
                        </label>
                        <input
                          id="name"
                          type="text"
                          autoComplete="name"
                          inputMode="text"
                          {...register('name')}
                          aria-invalid={!!errors.name}
                          className={`w-full px-4 py-3 bg-surface rounded-yada border ${
                            errors.name ? 'border-error' : 'border-border'
                          } focus:border-primary focus:outline-none transition-colors`}
                        />
                        {errors.name && (
                          <p className="mt-1 text-sm text-error flex items-center gap-1">
                            <Icon icon="lucide:alert-circle" />
                            {String(errors.name.message)}
                          </p>
                        )}
                      </div>

                      <div>
                        <label htmlFor="organization" className="block text-sm font-semibold text-ink mb-2">
                          {t('contact.form.org')} <span className="text-error">*</span>
                        </label>
                        <input
                          id="organization"
                          type="text"
                          autoComplete="organization"
                          inputMode="text"
                          {...register('organization')}
                          aria-invalid={!!errors.organization}
                          className={`w-full px-4 py-3 bg-surface rounded-yada border ${
                            errors.organization ? 'border-error' : 'border-border'
                          } focus:border-primary focus:outline-none transition-colors`}
                        />
                        {errors.organization && (
                          <p className="mt-1 text-sm text-error flex items-center gap-1">
                            <Icon icon="lucide:alert-circle" />
                            {String(errors.organization.message)}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Email + Phone */}
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="email" className="block text-sm font-semibold text-ink mb-2">
                          {t('contact.form.email')} <span className="text-error">*</span>
                        </label>
                        <input
                          id="email"
                          type="email"
                          autoComplete="email"
                          inputMode="email"
                          {...register('email')}
                          aria-invalid={!!errors.email}
                          className={`w-full px-4 py-3 bg-surface rounded-yada border ${
                            errors.email ? 'border-error' : 'border-border'
                          } focus:border-primary focus:outline-none transition-colors`}
                        />
                        {errors.email && (
                          <p className="mt-1 text-sm text-error flex items-center gap-1">
                            <Icon icon="lucide:alert-circle" />
                            {String(errors.email.message)}
                          </p>
                        )}
                      </div>

                      <div>
                        <label htmlFor="phone" className="block text-sm font-semibold text-ink mb-2">
                          {t('contact.form.phone')}
                        </label>
                        <input
                          id="phone"
                          type="tel"
                          autoComplete="tel"
                          inputMode="tel"
                          {...register('phone')}
                          className="w-full px-4 py-3 bg-surface rounded-yada border border-border focus:border-primary focus:outline-none transition-colors"
                        />
                      </div>
                    </div>

                    {/* Interests checkboxes */}
                    <div>
                      <span className="block text-sm font-semibold text-ink mb-3">
                        {t('contact.form.interests')}
                      </span>
                      <div className="grid sm:grid-cols-2 gap-3">
                        {products.map((p) => (
                          <label
                            key={p.id}
                            className="flex items-start gap-2 cursor-pointer p-3 bg-surface rounded-yada border border-border hover:border-primary transition-colors"
                          >
                            <input
                              type="checkbox"
                              value={p.id}
                              {...register('interests')}
                              className="mt-1 accent-primary"
                            />
                            <span className="text-sm text-ink">{t(p.titleKey)}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* Message */}
                    <div>
                      <label htmlFor="message" className="block text-sm font-semibold text-ink mb-2">
                        {t('contact.form.message')} <span className="text-error">*</span>
                      </label>
                      <textarea
                        id="message"
                        rows={5}
                        autoComplete="off"
                        inputMode="text"
                        {...register('message')}
                        aria-invalid={!!errors.message}
                        className={`w-full px-4 py-3 bg-surface rounded-yada border ${
                          errors.message ? 'border-error' : 'border-border'
                        } focus:border-primary focus:outline-none transition-colors resize-y`}
                      />
                      {errors.message && (
                        <p className="mt-1 text-sm text-error flex items-center gap-1">
                          <Icon icon="lucide:alert-circle" />
                          {String(errors.message.message)}
                        </p>
                      )}
                    </div>

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-primary text-surface px-8 py-4 rounded-yada-full hover:bg-primary-dark transition-all hover:-translate-y-0.5 text-base font-semibold shadow-yada disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
                    >
                      {isSubmitting ? (
                        <>
                          <Icon icon="lucide:loader-2" className="animate-spin" />
                          <span>{t('contact.form.submitting')}</span>
                        </>
                      ) : (
                        <>
                          <Icon icon="lucide:send" />
                          <span>{t('contact.form.submit')}</span>
                        </>
                      )}
                    </button>
                  </form>
                </div>
              </Reveal>
            </div>

            {/* Contact info */}
            <aside className="lg:col-span-2">
              <Reveal delay={0.1}>
                <div className="sticky top-24 space-y-4">
                  <h2 className="text-2xl font-bold text-ink mb-4">{t('contact.aside.title')}</h2>

                  <a
                    href={SITE.contact.whatsapp.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-start gap-4 p-4 bg-green-50 border-2 border-green-200 rounded-yada-lg hover:border-primary hover:shadow-yada transition-all group"
                  >
                    <div className="w-12 h-12 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0">
                      <Icon icon="lucide:message-circle" className="text-white text-2xl" />
                    </div>
                    <div>
                      <p className="font-bold text-ink">{t('contact.aside.whatsapp')}</p>
                      <p className="text-sm text-ink-soft">{SITE.contact.whatsapp.display}</p>
                      <p className="text-xs text-primary mt-1 group-hover:underline">{t('contact.aside.startChat')}</p>
                    </div>
                  </a>

                  <a
                    href={SITE.contact.email.link}
                    className="flex items-start gap-4 p-4 bg-surface-2 border border-border rounded-yada-lg hover:border-primary hover:shadow-yada transition-all"
                  >
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Icon icon="lucide:mail" className="text-primary text-2xl" />
                    </div>
                    <div>
                      <p className="font-bold text-ink">{t('contact.aside.email')}</p>
                      <p className="text-sm text-ink-soft break-all">{SITE.contact.email.display}</p>
                    </div>
                  </a>

                  <a
                    href={SITE.contact.website.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-start gap-4 p-4 bg-surface-2 border border-border rounded-yada-lg hover:border-primary hover:shadow-yada transition-all"
                  >
                    <div className="w-12 h-12 rounded-full bg-accent/20 flex items-center justify-center flex-shrink-0">
                      <Icon icon="lucide:globe" className="text-ink text-2xl" />
                    </div>
                    <div>
                      <p className="font-bold text-ink">{t('contact.aside.website')}</p>
                      <p className="text-sm text-ink-soft">{SITE.contact.website.display}</p>
                    </div>
                  </a>

                  <a
                    href={SITE.social.facebook.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-start gap-4 p-4 bg-surface-2 border border-border rounded-yada-lg hover:border-primary hover:shadow-yada transition-all"
                  >
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Icon icon="lucide:facebook" className="text-primary text-2xl" />
                    </div>
                    <div>
                      <p className="font-bold text-ink">{t('contact.aside.facebook')}</p>
                      <p className="text-sm text-ink-soft break-all">{SITE.social.facebook.handle}</p>
                    </div>
                  </a>

                  <div className="bg-yada-gold/10 border border-yada-gold/30 rounded-yada-lg p-4 mt-6">
                    <p className="text-sm text-ink-soft leading-relaxed">
                      <Icon icon="lucide:clock" className="inline mr-1" />
                      <strong className="text-ink">{t('contact.aside.replyTime')}</strong>
                      {t('contact.aside.replyText')}
                    </p>
                  </div>
                </div>
              </Reveal>
            </aside>
          </div>
        </Container>
      </section>
    </>
  )
}